import { useFormV2 } from '@abyss/web/hooks/useForm';
import { AlertService } from '@src/api/common/AlertService';
import { UrlService } from '@src/api/common/UrlService';
import { ActivityLogService } from '@src/api/web-services/features/admin/ActivityLogService';
import { IGridBuilderProps } from '@src/components/global/GridBuilder/interfaces/IGridBuilderProps';
import { AlertVariantType } from '@src/constants/AlertVariantType';
import { ApiStatus } from '@src/constants/ApiStatus';
import { BreadcrumbService } from '@src/api/common/BreadcrumbService';
import { PageTitleService } from '@src/api/common/PageTitleService';
import { DateService } from '@src/api/common/DateService';
import { Breadcrumbs, Title } from './PageInfo';
import { useEffect } from 'react';

type ActivityLogGridControllerProps = {
  sourceSessionId?: string | number | null;
};

export const ActivityLogGridController = ({
  sourceSessionId,
}: ActivityLogGridControllerProps = {}) => {
  const { AlertModel, ShowAlert } = AlertService();
  const { UpdateBreadcrumb } = BreadcrumbService();
  const { UpdatePageTitle } = PageTitleService();

  const form = useFormV2({
    defaultValues: {
      number: null,
      field: null,
      value: null,
      dateRange: null,
      startDate: '',
      endDate: '',
    },
  });

  const { GetAll } = ActivityLogService();

  const DataSource = {
    GridApiMethod: async (formObject: any = null) => {
      const body =
        formObject !== null ? formObject.getValues() : form.getValues();
      if (sourceSessionId) {
        body.number = sourceSessionId;
      }
      if (body.dateRange) {
        body.startDate = DateService.ToCustomFormat(
          body.dateRange.from,
          'YYYY-MM-DD'
        );
        body.endDate = DateService.ToCustomFormat(
          body.dateRange.to,
          'YYYY-MM-DD'
        );
        delete body.dateRange;
      }
      const queryString = UrlService.BuildQuery(body);
      const apiData = await GetAll(queryString);
      if (apiData.status === ApiStatus.Success) {
        return apiData.data.results;
      }
      AlertModel.variant = AlertVariantType.ERROR;
      AlertModel.message = apiData.message
        ? apiData.message
        : apiData.data.message;
      AlertModel.visible = true;
      AlertModel.isPermanent = true;
      await ShowAlert(AlertModel);

      return [];
    },
  };

  const gridProps: IGridBuilderProps = {
    gridName: 'activity-log-grid',
    columns: [
      { field: 'field', heading: 'Field', width: 300 },
      { field: 'value', heading: 'Value', width: 300 },
      {
        field: 'modifiedDate',
        heading: 'Updated Date',
        width: 300,
        isDate: true,
      },
      { field: 'modifiedBy', heading: 'Updated By', width: 300 },
    ],
    gridHeading: 'Activity Log',
    gridOptionName: 'activity-log-grid-options',
    gridApiMethod: DataSource.GridApiMethod,
    form,
  };

  const Action = {
    Init: async () => {
      await UpdateBreadcrumb(Breadcrumbs);
      await UpdatePageTitle(Title);
    },
  };

  useEffect(() => {
    Action.Init();
  }, []);

  return { form, gridProps };
};
